import type {
  AdminPagamentoDetalhado,
  AdminPagamentosEstatisticas,
  AdminPagamentosPageData,
  AdminPalworldOnlinePlayer,
  AdminPalworldJoinsPageData,
  AuthResponse,
  LoginCredentials,
  Pacote,
  PacoteInput,
  PalworldPerfilMe,
  PedidoMeu,
  VipUsuarioVigencia,
  WarzoneCoinsExtrato
} from '../types'
import type { CupomResponse } from '../types/cupons'
import type { PaymentResponse } from '../types/cart'
import type { PalworldSupplyPendente } from '../types/palworldSupply'
import { logoutEverywhere, saveAuthData } from '../utils/tokenUtils'

class ApiService {
  private async parseJsonOrThrow(response: Response): Promise<any> {
    const text = await response.text().catch(() => '')
    try {
      return text ? JSON.parse(text) : null
    } catch {
      const snippet = text.slice(0, 180)
      const err: any = new Error('Resposta inválida (esperado JSON). Verifique o baseUrl/proxy do backend.')
      err.status = response.status
      err.bodySnippet = snippet
      throw err
    }
  }

  private getBaseUrl(): string {
    // Em modo de desenvolvimento do Vite, usar URL relativa para passar pelo proxy (evita CORS).
    // Isso replica o comportamento esperado do ark-warzone em ambiente local.
    const viteMode = (import.meta as any)?.env?.MODE
    if (viteMode === 'development') {
      return ''
    }

    // Em produção, `VITE_API_BASE_URL` é injetado no build (não é runtime).
    // Observação importante: permitir string vazia para forçar URL relativa (ex.: com reverse-proxy no mesmo domínio).
    const envBaseRaw = (import.meta as any)?.env?.VITE_API_BASE_URL as string | undefined
    if (envBaseRaw !== undefined) {
      const envBase = String(envBaseRaw).trim()
      if (!envBase || envBase === '/') return ''
      return envBase.replace(/\/+$/, '')
    }

    // Heurística para redes locais (acesso via IPs privados) - manter URL relativa
    const host = window.location.hostname
    const isPrivateLAN =
      /^192\.168\./.test(host) ||
      /^10\./.test(host) ||
      /^172\.(1[6-9]|2\d|3[0-1])\./.test(host) ||
      host === 'localhost' ||
      host === '127.0.0.1'
    if (isPrivateLAN) return ''

    return 'https://api-ark.warzone-servers.com.br'
  }

  private withPalworldGame(url: string): string {
    const u = new URL(url, window.location.origin)
    u.searchParams.set('game', 'palworld')
    const isAbsolute = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url) || url.startsWith('//')
    return isAbsolute ? u.toString() : `${u.pathname}${u.search}${u.hash}`
  }

  private getAuthHeader(): Record<string, string> {
    const token = localStorage.getItem('accessToken')
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  private async throwForNonOk(response: Response): Promise<void> {
    if (response.ok) return

    if (response.status === 401) {
      logoutEverywhere()
    }

    const text = await response.text().catch(() => '')
    let message = `Erro HTTP: ${response.status}`
    const contentType = response.headers.get('content-type') || ''
    try {
      const json = text ? JSON.parse(text) : null
      message = json?.message || json?.error || json?.data?.error || json?.data?.message || message
    } catch {
      const snippet = text.slice(0, 180).trim()
      const looksLikeHtml =
        /text\/html/i.test(contentType) ||
        /^<!doctype html/i.test(snippet) ||
        /^<html/i.test(snippet)
      if (looksLikeHtml) {
        message = `Resposta não-JSON do servidor (HTTP ${response.status}). Verifique se o endpoint existe e se o proxy/baseUrl está correto.`
      } else if (snippet) {
        message = snippet
      }
    }

    const err: any = new Error(message)
    err.status = response.status
    err.body = text
    throw err
  }

  private unwrapApiData<T>(raw: any): T {
    if (!raw) throw new Error('Resposta vazia do servidor.')
    if (raw?.success === false) {
      const msg = raw?.message || raw?.error || raw?.data?.error || raw?.data?.message || 'Operação não realizada.'
      throw new Error(msg)
    }
    if (Object.prototype.hasOwnProperty.call(raw, 'data')) return raw.data as T
    return raw as T
  }

  private async fetchWith404Fallback(url: string, fallbackUrl: string, init: () => RequestInit): Promise<Response> {
    const r1 = await fetch(url, init())
    if (r1.status !== 404) return r1
    return fetch(fallbackUrl, init())
  }

  async listarPalworldJoinsAdmin(params?: {
    page?: number
    size?: number
    sort?: string
    direction?: 'asc' | 'desc'
    dataInicial?: string
    dataFinal?: string
  }): Promise<AdminPalworldJoinsPageData> {
    const page = params?.page ?? 0
    const size = params?.size ?? 50
    const sort = params?.sort ?? 'created_at'
    const direction = params?.direction ?? 'desc'

    const u = new URL(`${this.getBaseUrl()}/api/admin/palworld/joins`, window.location.origin)
    u.searchParams.set('page', String(page))
    u.searchParams.set('size', String(size))
    u.searchParams.set('sort', sort)
    u.searchParams.set('direction', direction)
    if (params?.dataInicial) u.searchParams.set('dataInicial', params.dataInicial)
    if (params?.dataFinal) u.searchParams.set('dataFinal', params.dataFinal)
    u.searchParams.set('_ts', String(Date.now()))

    const response = await fetch(u.toString(), {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<AdminPalworldJoinsPageData>(raw)
  }

  async listarPalworldOnlineAdmin(): Promise<AdminPalworldOnlinePlayer[]> {
    const u = new URL(`${this.getBaseUrl()}/api/admin/palworld/online`, window.location.origin)
    u.searchParams.set('_ts', String(Date.now()))

    const response = await fetch(u.toString(), {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    const data = this.unwrapApiData<any>(raw)
    return Array.isArray(data) ? (data as AdminPalworldOnlinePlayer[]) : []
  }

  getAuthenticateUrl(): string {
    const baseURL = this.getBaseUrl()
    return `${baseURL}/api/auth/authenticate`
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const url = this.getAuthenticateUrl()
    const payload: LoginCredentials & { game: string } = { ...credentials, game: 'palworld' }

    if ((import.meta as any)?.env?.DEV) {
      // Ajuda a diagnosticar CORS/proxy: o esperado no dev é url começar com "/api/..."
      // Ex.: "/api/auth/authenticate" (proxy do Vite) e NÃO "http://localhost:8080/..."
      console.debug(
        '[api] dev:',
        (import.meta as any)?.env?.DEV,
        'mode:',
        (import.meta as any)?.env?.MODE,
        'envBase:',
        (import.meta as any)?.env?.VITE_API_BASE_URL,
        'origin:',
        window.location.origin
      )
      console.debug('[api] login url:', url)
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      let errorBody: any = null
      try {
        errorBody = await response.json()
      } catch {
        // ignore
      }

      const message =
        errorBody?.message ||
        errorBody?.error ||
        `Erro HTTP: ${response.status}`

      const err: any = new Error(message)
      err.status = response.status
      err.data = errorBody
      throw err
    }

    const data = (await this.parseJsonOrThrow(response)) as AuthResponse

    if (data?.accessToken) {
      saveAuthData({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        userId: data.userId,
        username: credentials.username,
        roles: data.roles
      })
    }

    return data
  }

  async listarUsuariosAdmin(params?: {
    page?: number
    size?: number
    sort?: string
    direction?: 'asc' | 'desc'
    email?: string
    playerName?: string
    active?: boolean
  }): Promise<any> {
    // Endpoint específico do Palworld (retorna lista simples, sem paginação server-side).
    // Mantemos a assinatura com `params` por compatibilidade com a tela existente.
    void params
    const url = `${this.getBaseUrl()}/api/admin/usuarios/palworld`
    const token = localStorage.getItem('accessToken')

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: 'application/json'
      }
    })

    if (!response.ok) {
      if (response.status === 401) logoutEverywhere()
      const text = await response.text().catch(() => '')
      const err: any = new Error(`Erro HTTP: ${response.status}`)
      err.status = response.status
      err.body = text
      throw err
    }

    return this.parseJsonOrThrow(response)
  }

  async listarUsuariosPalworldAccountNames(): Promise<Array<{ id: string; name: string }>> {
    const base = this.getBaseUrl()
    const url = `${base}/api/admin/usuarios/palworld/account-names`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    const data = this.unwrapApiData<any>(raw)
    if (!Array.isArray(data)) return []

    const out: Array<{ id: string; name: string }> = []
    for (const u of data as any[]) {
      const id = u?.id != null ? String(u.id).trim() : ''
      const nameRaw =
        u?.pal_account_name ??
        u?.pal_player_name ??
        u?.account_name ??
        u?.accountName ??
        u?.name ??
        ''
      const name = String(nameRaw ?? '').trim()
      if (!id || !name) continue
      out.push({ id, name })
    }
    return out
  }

  async listarUsuariosPalworldPlayerNames(): Promise<Array<{ id: string; pal_player_name: string }>> {
    const data = await this.listarUsuariosPalworldAccountNames()
    return data.map((u) => ({ id: u.id, pal_player_name: u.name }))
  }

  async enviarPalworldItensGratisAdmin(payload: {
    userId: string
    reason: string
    items: Array<{ itemId: string; quantity: number }>
  }): Promise<any> {
    const url = `${this.getBaseUrl()}/api/admin/palworld/supply/enviar`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return raw === null ? raw : this.unwrapApiData<any>(raw)
  }

  async listarUsuariosVipVigenciaPalworld(): Promise<VipUsuarioVigencia[]> {
    const qs = new URLSearchParams({ _ts: String(Date.now()) })
    const url = `${this.getBaseUrl()}/api/vip/palworld/usuarios/vigencia?${qs.toString()}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    const data = this.unwrapApiData<any>(raw)

    if (Array.isArray(data)) return data as VipUsuarioVigencia[]
    if (Array.isArray(data?.content)) return data.content as VipUsuarioVigencia[]
    if (Array.isArray(raw?.content)) return raw.content as VipUsuarioVigencia[]
    return []
  }

  async concederVipPalworldAdmin(
    userId: string,
    payload: { diasVip: number; motivo: string }
  ): Promise<any> {
    if (!userId) throw new Error('userId é obrigatório.')

    const base = this.getBaseUrl()
    const encodedId = encodeURIComponent(userId)
    const url = `${base}/api/admin/vip/palworld/conceder/${encodedId}`
    const fallbackUrl = `${base}/admin/vip/palworld/conceder/${encodedId}`
    const body = JSON.stringify(payload)

    const response = await this.fetchWith404Fallback(url, fallbackUrl, () => ({
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body
    }))

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return raw === null ? raw : this.unwrapApiData<any>(raw)
  }

  async creditarWarzoneCoinsAdmin(payload: { userId: string; quantidade: number; descricao: string }): Promise<any> {
    const userId = payload?.userId
    if (!userId) throw new Error('userId é obrigatório.')

    const quantidade = payload?.quantidade
    if (!Number.isFinite(quantidade) || !Number.isInteger(quantidade) || quantidade <= 0) {
      throw new Error('quantidade deve ser um inteiro maior que 0.')
    }

    const descricao = String(payload?.descricao ?? '').trim()
    if (!descricao) throw new Error('descricao é obrigatória.')

    const base = this.getBaseUrl()
    const url = `${base}/api/admin/warzone-coins/creditar`
    const fallbackUrl = `${base}/admin/warzone-coins/creditar`

    const body = JSON.stringify({ userId, quantidade, descricao })

    const response = await this.fetchWith404Fallback(url, fallbackUrl, () => ({
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body
    }))

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return raw === null ? raw : this.unwrapApiData<any>(raw)
  }

  async debitarWarzoneCoinsAdmin(payload: { userId: string; quantidade: number; descricao: string }): Promise<any> {
    const userId = payload?.userId
    if (!userId) throw new Error('userId é obrigatório.')

    const quantidade = payload?.quantidade
    if (!Number.isFinite(quantidade) || !Number.isInteger(quantidade) || quantidade <= 0) {
      throw new Error('quantidade deve ser um inteiro maior que 0.')
    }

    const descricao = String(payload?.descricao ?? '').trim()
    if (!descricao) throw new Error('descricao é obrigatória.')

    const base = this.getBaseUrl()
    const url = `${base}/api/admin/warzone-coins/debitar`
    const fallbackUrl = `${base}/admin/warzone-coins/debitar`

    const body = JSON.stringify({ userId, quantidade, descricao })

    const response = await this.fetchWith404Fallback(url, fallbackUrl, () => ({
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body
    }))

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return raw === null ? raw : this.unwrapApiData<any>(raw)
  }

  async obterUsuarioPalworldCompletoAdmin(params: {
    userId: string
    extratoPage?: number
    extratoSize?: number
  }): Promise<any> {
    const userId = params.userId
    if (!userId) throw new Error('userId é obrigatório.')

    const extratoPage = params.extratoPage ?? 0
    const extratoSize = params.extratoSize ?? 200

    const base = this.getBaseUrl()
    const encodedId = encodeURIComponent(userId)
    const u1 = new URL(`${base}/api/admin/usuarios/${encodedId}/palworld/completo`, window.location.origin)
    u1.searchParams.set('extratoPage', String(extratoPage))
    u1.searchParams.set('extratoSize', String(extratoSize))
    u1.searchParams.set('_ts', String(Date.now()))

    const u2 = new URL(`${base}/admin/usuarios/${encodedId}/palworld/completo`, window.location.origin)
    u2.searchParams.set('extratoPage', String(extratoPage))
    u2.searchParams.set('extratoSize', String(extratoSize))
    u2.searchParams.set('_ts', String(Date.now()))

    const response = await this.fetchWith404Fallback(u1.toString(), u2.toString(), () => ({
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    }))

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return raw === null ? raw : this.unwrapApiData<any>(raw)
  }

  async listarPagamentosAdmin(params?: {
    page?: number
    size?: number
    sort?: string
    direction?: 'asc' | 'desc'
    status?: string
    tipoPagamento?: string
    dataInicio?: string
    dataFim?: string
  }): Promise<AdminPagamentosPageData> {
    const page = params?.page ?? 0
    const size = params?.size ?? 10
    const sort = params?.sort ?? 'createdAt'
    const direction = params?.direction ?? 'desc'

    const base = this.getBaseUrl()
    const u = new URL(`${base}/api/admin/pagamentos`, window.location.origin)
    u.searchParams.set('page', String(page))
    u.searchParams.set('size', String(size))
    u.searchParams.set('sort', sort)
    u.searchParams.set('direction', direction)
    if (params?.status) u.searchParams.set('status', params.status)
    if (params?.tipoPagamento) u.searchParams.set('tipoPagamento', params.tipoPagamento)
    if (params?.dataInicio) u.searchParams.set('dataInicio', params.dataInicio)
    if (params?.dataFim) u.searchParams.set('dataFim', params.dataFim)
    u.searchParams.set('_ts', String(Date.now()))

    const url = this.withPalworldGame(u.toString())

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<AdminPagamentosPageData>(raw)
  }

  async buscarPagamentoAdminPorId(id: string): Promise<AdminPagamentoDetalhado> {
    const base = this.getBaseUrl()
    const u = new URL(`${base}/api/admin/pagamentos/${encodeURIComponent(id)}`, window.location.origin)
    u.searchParams.set('_ts', String(Date.now()))
    const url = this.withPalworldGame(u.toString())

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<AdminPagamentoDetalhado>(raw)
  }

  async obterEstatisticasPagamentosAdmin(params?: {
    status?: string
    dataInicio?: string
    dataFim?: string
  }): Promise<AdminPagamentosEstatisticas> {
    const base = this.getBaseUrl()
    const u = new URL(`${base}/api/admin/pagamentos/estatisticas`, window.location.origin)
    if (params?.status) u.searchParams.set('status', params.status)
    if (params?.dataInicio) u.searchParams.set('dataInicio', params.dataInicio)
    if (params?.dataFim) u.searchParams.set('dataFim', params.dataFim)
    u.searchParams.set('_ts', String(Date.now()))
    const url = this.withPalworldGame(u.toString())

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<AdminPagamentosEstatisticas>(raw)
  }

  async listarPalworldItems(params?: {
    page?: number
    size?: number
    sort?: string
    direction?: 'asc' | 'desc'
    name?: string
    category?: string
    rank?: number
    rarity?: number
    active?: boolean
  }): Promise<any> {
    const page = params?.page ?? 0
    const size = params?.size ?? 20
    const sort = params?.sort ?? 'price'

    const qs = new URLSearchParams({
      page: String(page),
      size: String(size)
    })

    if (sort) qs.set('sort', sort)
    if (params?.direction) qs.set('direction', params.direction)

    if (params?.name) qs.set('name', params.name)
    if (params?.category) qs.set('category', params.category)
    if (params?.rank !== undefined) qs.set('rank', String(params.rank))
    if (params?.rarity !== undefined) qs.set('rarity', String(params.rarity))
    if (params?.active !== undefined) qs.set('active', String(params.active))

    const url = `${this.getBaseUrl()}/api/palworld/items?${qs.toString()}`
    const token = localStorage.getItem('accessToken')

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: 'application/json'
      }
    })

    if (!response.ok) {
      if (response.status === 401) logoutEverywhere()
      const text = await response.text().catch(() => '')
      const err: any = new Error(`Erro HTTP: ${response.status}`)
      err.status = response.status
      err.body = text
      throw err
    }

    return this.parseJsonOrThrow(response)
  }

  async listarPalworldItemCategories(): Promise<string[]> {
    const url = `${this.getBaseUrl()}/api/palworld/items/categories`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<string[]>(raw)
  }

  async setPalworldItemActive(idItem: string, active: boolean): Promise<any> {
    const url = `${this.getBaseUrl()}/api/admin/palworld/items/${encodeURIComponent(idItem)}/active`

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ active })
    })

    await this.throwForNonOk(response)
    return this.parseJsonOrThrow(response)
  }

  async atualizarPalworldItem(idItem: string, payload: {
    assetName?: string
    category?: string
    description?: string
    name?: string
    price?: number
    rank?: number
    rarity?: number
    stackSize?: number
    typeA?: string
    typeB?: string
    weight?: number
    active?: boolean
    imageS3?: { bucket: string; key: string }
  }): Promise<any> {
    const url = `${this.getBaseUrl()}/api/admin/palworld/items/${encodeURIComponent(idItem)}`

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })

    await this.throwForNonOk(response)
    return this.parseJsonOrThrow(response)
  }

  async criarKitInicial(payload: {
    nome: string
    descricao: string
    itens: Array<{ itemId: string; quantidade: number }>
  }): Promise<any> {
    const url = `${this.getBaseUrl()}/api/admin/palworld/kit-inicial`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return raw === null ? raw : this.unwrapApiData<any>(raw)
  }

  async listarKitsIniciais(params?: { active?: boolean }): Promise<any> {
    const qs = new URLSearchParams()
    if (params?.active !== undefined) qs.set('active', String(params.active))
    const url = `${this.getBaseUrl()}/api/admin/palworld/kit-inicial${qs.toString() ? `?${qs.toString()}` : ''}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<any>(raw)
  }

  async buscarKitInicialPorId(id: string): Promise<any> {
    const url = `${this.getBaseUrl()}/api/admin/palworld/kit-inicial/${encodeURIComponent(id)}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<any>(raw)
  }

  async atualizarKitInicial(id: string, payload: {
    nome: string
    descricao: string
    itens: Array<{ itemId: string; quantidade: number }>
  }): Promise<any> {
    const url = `${this.getBaseUrl()}/api/admin/palworld/kit-inicial/${encodeURIComponent(id)}`

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return raw === null ? raw : this.unwrapApiData<any>(raw)
  }

  async getWarzoneCoinsExtrato(params?: { page?: number; size?: number }): Promise<WarzoneCoinsExtrato> {
    const page = params?.page ?? 0
    const size = params?.size ?? 50

    const qs = new URLSearchParams({
      page: String(page),
      size: String(size)
    })

    const url = `${this.getBaseUrl()}/api/warzone-coins/extrato?${qs.toString()}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<WarzoneCoinsExtrato>(raw)
  }

  async getMeuPerfilPalworld(): Promise<PalworldPerfilMe> {
    const url = `${this.getBaseUrl()}/api/usuarios/me/palworld/perfil`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<PalworldPerfilMe>(raw)
  }

  async getMeusPedidos(params?: { game?: string }): Promise<PedidoMeu[]> {
    const game = params?.game ?? 'palworld'
    const qs = new URLSearchParams({ game })
    const url = `${this.getBaseUrl()}/api/pedidos/meus?${qs.toString()}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<PedidoMeu[]>(raw)
  }

  async getPalworldSupplyPendentes(): Promise<PalworldSupplyPendente[]> {
    const url = `${this.getBaseUrl()}/api/usuarios/me/palworld/supply/pendentes`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<PalworldSupplyPendente[]>(raw)
  }

  async entregarPalworldSupplyPendentes(): Promise<any> {
    const url = `${this.getBaseUrl()}/api/usuarios/me/palworld/supply/entregar`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return raw === null ? raw : this.unwrapApiData<any>(raw)
  }

  async comprarPalworldItems(payload: {
    items: Array<{ itemId: string; quantity: number }>
  }): Promise<any> {
    const base = this.getBaseUrl()
    const url = `${base}/api/palworld/items/comprar`

    const response = await fetch(url || '/api/palworld/items/comprar', {
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return raw === null ? raw : this.unwrapApiData<any>(raw)
  }

  async listarPacotes(): Promise<Pacote[]> {
    const base = this.getBaseUrl()
    const url = this.withPalworldGame(`${base}/api/pacotes`)
    const legacyUrl = this.withPalworldGame(`${base}/pacotes`)

    const response = await this.fetchWith404Fallback(url, legacyUrl, () => ({
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    }))

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<Pacote[]>(raw)
  }

  async buscarPacote(id: string): Promise<Pacote> {
    const base = this.getBaseUrl()
    const url = this.withPalworldGame(`${base}/api/pacotes/${encodeURIComponent(id)}`)
    const legacyUrl = this.withPalworldGame(`${base}/pacotes/${encodeURIComponent(id)}`)

    const response = await this.fetchWith404Fallback(url, legacyUrl, () => ({
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    }))

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<Pacote>(raw)
  }

  async criarPacote(pacote: PacoteInput, imagem: File): Promise<Pacote> {
    const base = this.getBaseUrl()
    const url = this.withPalworldGame(`${base}/api/pacotes`)
    const legacyUrl = this.withPalworldGame(`${base}/pacotes`)

    const response = await this.fetchWith404Fallback(url, legacyUrl, () => {
      const form = new FormData()
      form.append('pacote', new Blob([JSON.stringify(pacote)], { type: 'application/json' }))
      form.append('imagem', imagem)
      return {
        method: 'POST',
        headers: {
          ...this.getAuthHeader(),
          Accept: 'application/json'
        },
        body: form
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<Pacote>(raw)
  }

  async criarPacoteVip(pacote: PacoteInput): Promise<Pacote> {
    const base = this.getBaseUrl()
    const url = this.withPalworldGame(`${base}/api/pacotes/vip`)
    const legacyUrl = this.withPalworldGame(`${base}/pacotes/vip`)

    const body = JSON.stringify(pacote)
    const response = await this.fetchWith404Fallback(url, legacyUrl, () => ({
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body
    }))

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<Pacote>(raw)
  }

  async atualizarPacote(id: string, pacote: PacoteInput): Promise<Pacote> {
    const base = this.getBaseUrl()
    const url = this.withPalworldGame(`${base}/api/pacotes/${encodeURIComponent(id)}`)
    const legacyUrl = this.withPalworldGame(`${base}/pacotes/${encodeURIComponent(id)}`)

    const body = JSON.stringify(pacote)
    const response = await this.fetchWith404Fallback(url, legacyUrl, () => ({
      method: 'PUT',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body
    }))

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<Pacote>(raw)
  }

  async atualizarPacoteImagem(id: string, imagem: File): Promise<Pacote> {
    const base = this.getBaseUrl()
    const url = this.withPalworldGame(`${base}/api/pacotes/${encodeURIComponent(id)}/imagem`)
    const legacyUrl = this.withPalworldGame(`${base}/pacotes/${encodeURIComponent(id)}/imagem`)

    const response = await this.fetchWith404Fallback(url, legacyUrl, () => {
      const form = new FormData()
      form.append('imagem', imagem)
      return {
        method: 'PATCH',
        headers: {
          ...this.getAuthHeader(),
          Accept: 'application/json'
        },
        body: form
      }
    })

    await this.throwForNonOk(response)
    const raw = await this.parseJsonOrThrow(response)
    return this.unwrapApiData<Pacote>(raw)
  }

  async apagarPacote(id: string): Promise<void> {
    const base = this.getBaseUrl()
    const url = this.withPalworldGame(`${base}/api/pacotes/${encodeURIComponent(id)}`)
    const legacyUrl = this.withPalworldGame(`${base}/pacotes/${encodeURIComponent(id)}`)

    const response = await this.fetchWith404Fallback(url, legacyUrl, () => ({
      method: 'DELETE',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    }))

    await this.throwForNonOk(response)
  }

  async buscarCupomPorCodigo(codigo: string): Promise<CupomResponse> {
    const base = this.getBaseUrl()
    const url = `${base}/api/cupons/codigo/${encodeURIComponent(codigo)}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json'
      }
    })

    await this.throwForNonOk(response)
    return this.parseJsonOrThrow(response)
  }

  async createPixPayment(paymentData: {
    userId: string
    pacoteIds: string[]
    tipoPagamento: 'PIX_DIRETO'
    cupomCodigo?: string
  }): Promise<PaymentResponse> {
    const base = this.getBaseUrl()
    const url = this.withPalworldGame(`${base}/api/pagamentos`)

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(paymentData)
    })

    await this.throwForNonOk(response)
    return this.parseJsonOrThrow(response)
  }

  async createCardPayment(
    userId: string,
    pacoteIds: string[],
    cupomCodigo: string | undefined,
    creditCardData: {
      customerName: string
      cpf: string
      email: string
      phoneNumber: string
      installments: number
    },
    paymentToken: string
  ): Promise<PaymentResponse> {
    const base = this.getBaseUrl()
    const url = this.withPalworldGame(`${base}/api/pagamentos/cartao-credito`)

    const payload = {
      userId,
      pacoteIds,
      ...(cupomCodigo ? { cupomCodigo } : {}),
      payment: {
        credit_card: {
          customer: {
            name: creditCardData.customerName,
            cpf: creditCardData.cpf.replace(/\D/g, ''),
            email: creditCardData.email,
            phone_number: creditCardData.phoneNumber.replace(/\D/g, '')
          },
          installments: creditCardData.installments,
          payment_token: paymentToken
        }
      }
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })

    await this.throwForNonOk(response)
    return this.parseJsonOrThrow(response)
  }
}

export const apiService = new ApiService()
